import type { Worksheet } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import type { HospitalData } from "../../../utils/models";
import { groupRows } from "../group/sectionGrouping";
import type {
	ActiveCustomTimeGroups,
	CustomColumnNamesConfig,
	DailyPrintoutGrouping,
	NurseInShiftWithPto,
	PrintoutColumn,
	PrintoutColumnType,
	RowNoteFlags,
	ScheduleRow,
} from "../types";
import { compositeGroupingKeyToDisplayName } from "../util/groupingDisplay";
import { formatHourMinute } from "../util/shiftTimeFormat";
import { renderStandardSectionHeader, renderSubheader } from "./sectionHeader";
import { renderStandardSectionRows } from "./standardSectionRows";
import { applyVerticalMerges } from "./verticalMerges";

interface RenderPtoSectionArgs {
	worksheet: Worksheet;
	currentRow: number;
	ptoShifts: NurseInShiftWithPto[];
	hospitalData: HospitalData;
	department: Department;
	timezone: string;
	jobManagementEnabled: boolean;
	rowGroupingBy: PrintoutColumnType[];
	printoutGroupBy: DailyPrintoutGrouping;
	staffPerRow: number;
	expandedColumns: PrintoutColumn[];
	noteFlags: RowNoteFlags | undefined;
	sortBy: PrintoutColumn[] | undefined;
	customStringOrderMap: Map<string, number> | null | undefined;
	appendRoleToName: boolean;
	customColumnNames: CustomColumnNamesConfig;
	/** Leave out the PTO table's column-header row (the "Time Off / PTO" title and subheaders stay). */
	hideColumnNames: boolean;
	/** Start Time subheaders follow the custom groups like the staff sections; null when the switch is off. */
	customTimeGroups: ActiveCustomTimeGroups | null;
}

/** Columns shown inside the PTO section. Other column kinds (initials, floor) aren't relevant for PTO. */
const PTO_COLUMN_TYPES = new Set<PrintoutColumnType>(["name", "certification", "location", "shiftTime", "notes", "role"]);

/**
 * Convert a PTO shift into a ScheduleRow for rendering. PTO rows have `isPtoShift: false` so
 * the strike-through styling doesn't apply (PTO is the section, not a per-row decoration here).
 */
const ptoShiftToScheduleRow = (shift: NurseInShiftWithPto): ScheduleRow => {
	return {
		location: shift.location || "",
		section: "",
		nurse: shift,
		role: shift.role,
		startHour: shift.startTime.hour,
		shiftKey: formatHourMinute(shift.startTime.hour, shift.startTime.minute),
		shiftStart: shift.startTime,
		shiftEnd: shift.endTime,
		floor: undefined,
	};
};

/**
 * Render the "Time Off / PTO" section at the bottom of the sheet (mapping §9.3).
 *
 * Layout:
 * - One blank spacer row
 * - Standard section header "Time Off / PTO" + column headers (the latter left out under `hideColumnNames`)
 * - One subheader + data rows per PTO group (groups follow the same `printoutGroupBy` as the
 *   staff sections above)
 *
 * `includeTimeOffReason` widens the effective `printNotes` so that PTO rows always show their
 * notes column (where the reason is rendered). Vertical merges apply to the PTO `rowGroupingBy`
 * plus implicit `name` merging (PTO often groups multiple shifts for the same nurse).
 *
 * Appended blank columns (`appendEmptyColumns`) never reach this table — they belong to the staff
 * sections only.
 *
 * Returns the row after the PTO section, or the input row unchanged if there are no PTO shifts.
 */
export const renderPtoSection = (args: RenderPtoSectionArgs): number => {
	const {
		worksheet,
		currentRow: initialRow,
		ptoShifts,
		hospitalData,
		department,
		timezone,
		jobManagementEnabled,
		rowGroupingBy,
		printoutGroupBy,
		staffPerRow,
		expandedColumns,
		noteFlags,
		sortBy,
		customStringOrderMap,
		appendRoleToName,
		customColumnNames,
		hideColumnNames,
		customTimeGroups,
	} = args;

	if (ptoShifts.length === 0) return initialRow;

	const ptoColumns = expandedColumns.filter((col) => PTO_COLUMN_TYPES.has(col.type));
	// PTO always renders a Notes column (trailing if the user didn't include it in their column
	// selection). The reason a nurse is off — vacation, sick, etc. — surfaces there, and it's the
	// PTO section's primary data point. The `printNotes` / `includeTimeOffReason` flags only gate
	// whether the *staff* section gets notes; PTO is unconditional.
	const effectivePrintNotes = true;

	let currentRow = initialRow + 1;
	currentRow = renderStandardSectionHeader({
		worksheet,
		title: "Time Off / PTO",
		currentRow,
		expandedColumns: ptoColumns,
		printNotes: effectivePrintNotes,
		jobManagementEnabled,
		customColumnNames,
		hideColumnNames,
	});

	const ptoScheduleRows = ptoShifts.map(ptoShiftToScheduleRow);
	// Staff sections keep empty custom time groups so every group prints; a time-off list only lists
	// the groups someone is off in (without custom groups no section is ever empty).
	const ptoSections = groupRows({
		rows: ptoScheduleRows,
		printoutGroupBy,
		jobManagementEnabled,
		customStringOrderMap,
		sortBy,
		customTimeGroups,
	}).filter((section) => section.rows.length > 0);

	const hasNotesInColumns = ptoColumns.some((col) => col.type === "notes");
	const totalColumns = ptoColumns.length + (effectivePrintNotes && !hasNotesInColumns ? 1 : 0);
	const ptoMergeBy: PrintoutColumnType[] = rowGroupingBy.includes("name") ? rowGroupingBy : [...rowGroupingBy, "name"];

	for (const section of ptoSections) {
		const displayName = compositeGroupingKeyToDisplayName(
			section.groupingKey,
			printoutGroupBy,
			jobManagementEnabled,
			department,
			undefined,
			customTimeGroups?.labels,
		);
		currentRow = renderSubheader({ worksheet, currentRow, title: displayName, columnCount: totalColumns });

		const dataStart = currentRow;
		currentRow = renderStandardSectionRows({
			worksheet,
			rows: section.rows,
			currentRow,
			enabledColumns: ptoColumns,
			expandedColumns: ptoColumns,
			hospitalData,
			department,
			timezone,
			jobManagementEnabled,
			printNotes: effectivePrintNotes,
			staffPerRow,
			rowGroupingBy,
			noteFlags,
			sortBy,
			customStringOrderMap,
			appendRoleToName,
		});
		applyVerticalMerges({ worksheet, startRow: dataStart, endRow: currentRow - 1, expandedColumns: ptoColumns, rowGroupingBy: ptoMergeBy });

		if (ptoSections.length > 1) currentRow += 1;
	}

	return currentRow;
};
